
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { buildEtsyOAuthUrl } from '@/utils/etsyAuth';
import { supabase } from '@/integrations/supabase/client';
import { getCachedUserData } from '@/utils/userCache';
import { clearCache } from '@/utils/etsyCache';
import { checkEtsyConnection } from '@/utils/etsyConnection';

export const useEtsyConnection = (hasActiveSubscription: boolean) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { toast } = useToast();

  const checkExistingStoreWithIP = async (): Promise<boolean> => {
    try {
      const user = await getCachedUserData();
      if (!user) return false;

      // Get the current user's IP (this is a simplified approach)
      // In a real scenario, you might want to get the IP from a service
      const response = await fetch('https://api.ipify.org?format=json');
      const { ip: currentIP } = await response.json();

      // Check if user already has a store with this IP
      const { data: existingStores, error } = await supabase
        .from('etsy_shops')
        .select('id, shop_name, ip_address')
        .eq('user_id', user.id)
        .eq('ip_address', currentIP);

      if (error) {
        console.error('Error checking existing stores:', error);
        return false;
      }

      return existingStores && existingStores.length > 0;
    } catch (error) {
      console.error('Error checking IP:', error);
      return false;
    }
  };

  const connectEtsy = async () => {
    if (!hasActiveSubscription) {
      toast({
        title: "Subscription Required",
        description: "You need an active subscription to connect to Etsy",
        variant: "destructive",
      });
      return;
    }

    setIsConnecting(true);
    
    try {
      // Check if user already has a store with the current IP
      const hasExistingStoreWithIP = await checkExistingStoreWithIP();
      
      if (hasExistingStoreWithIP) {
        toast({
          title: "Store Already Connected",
          description: "You already have an Etsy store connected from this IP address. Please disconnect your existing store or contact support if you need to change your IP address before integrating a new store.",
          variant: "destructive",
        });
        setIsConnecting(false);
        return;
      }

      const authUrl = await buildEtsyOAuthUrl();
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Etsy connection error:', error);
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to connect to Etsy",
        variant: "destructive",
      });
      setIsConnecting(false);
    }
  };

  const disconnectEtsy = async () => {
    setIsDisconnecting(true);
    try {
      const user = await getCachedUserData();
      if (!user) throw new Error('User not authenticated');

      // Remove the Etsy shop connection from the database
      const { error } = await supabase
        .from('etsy_shops')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      // Clear cached orders and localStorage data
      clearCache();

      toast({
        title: "Disconnected",
        description: "Successfully disconnected from Etsy",
      });

      return true; // Indicate successful disconnection
    } catch (error: any) {
      console.error('Error disconnecting from Etsy:', error);
      toast({
        title: "Disconnect Failed",
        description: error.message || "Failed to disconnect from Etsy",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsDisconnecting(false);
    }
  };

  const checkConnection = () => checkEtsyConnection(hasActiveSubscription);

  return {
    connectEtsy,
    disconnectEtsy,
    checkEtsyConnection: checkConnection,
    isConnecting,
    isDisconnecting,
  };
};
